// Report-specific i18n strings that are ONLY used on the Report page and by
// the study-plan clipboard export. Kept out of the main `i18n.js` bundle so
// the strings live next to the component that owns them.
//
// Every key ships in all four supported app languages. Missing keys resolve
// through `reportT()` which falls back to ENGLISH (never Portuguese) — this
// matches the localisation spec: "When a translation key is missing, do not
// silently fall back to Portuguese."

import { normalizeLang } from "./i18n";

const REPORT_STRINGS = {
  en: {
    "rp.loading": "Building your report…",
    "rp.copyOk": "Plan copied to clipboard",
    "rp.copyFail": "Couldn't copy — try selecting the text manually.",
    "rp.planError": "Could not create the plan.",
    // Coaching level headline (0-100 buckets) + matching copy paragraph.
    "rp.levelExcellent": "Excellent",
    "rp.levelStrong": "Strong",
    "rp.levelDeveloping": "Developing",
    "rp.levelNeedsAttention": "Needs Attention",
    "rp.levelPriority": "Development Priority",
    "rp.copyExcellent":
      "You show a very strong level of professional English communication. Keep refining your answers to maintain this standard.",
    "rp.copyStrong":
      "You demonstrate solid, professional communication. Small polish will make your answers even more impactful.",
    "rp.copyDeveloping":
      "You have a good foundation in professional English. With targeted practice in the areas highlighted in this report, you can raise your performance significantly.",
    "rp.copyNeedsAttention":
      "Some competencies need more development before your next English interview. Follow the study plan to strengthen these areas.",
    "rp.copyPriority":
      "We recommend spending more time on the highlighted competencies before attempting a live English interview. The study plan is designed to guide you step by step.",
    // Best-area praise / worst-area coaching, one line per competency.
    "rp.bestFor.relevance": "Relevance — you answered directly to the point.",
    "rp.bestFor.supporting_evidence": "Supporting evidence — you backed your answer with concrete examples.",
    "rp.bestFor.organization": "Organization — your response followed a clear, logical sequence.",
    "rp.bestFor.professional_competency": "Professional competency — you showed the knowledge and behaviour expected.",
    "rp.bestFor.evidence_of_results": "Evidence of results — you showed the real impact of your actions.",
    "rp.bestFor.professional_presence": "Professional presence — you communicated with confidence and ownership.",
    "rp.bestFor.clarity": "Clarity — you speak in a way that's easy to follow.",
    "rp.bestFor.grammar": "Grammar — your sentence structure is solid.",
    "rp.bestFor.vocabulary": "Vocabulary — you reach for the right words.",
    "rp.bestFor.professional_tone": "Professional tone — you sound like the role.",
    "rp.bestFor.fluency": "Fluency — your spoken English flows naturally without unnecessary hesitation.",
    "rp.worstFor.relevance": "Relevance — read the whole question and address every part.",
    "rp.worstFor.supporting_evidence": "Supporting evidence — use concrete situations, not generic descriptions.",
    "rp.worstFor.organization": "Organization — use a clear sequence (context → action → result).",
    "rp.worstFor.professional_competency": "Professional competency — showcase decisions and knowledge typical for the role.",
    "rp.worstFor.evidence_of_results": "Evidence of results — end every example with the outcome or impact.",
    "rp.worstFor.professional_presence": "Professional presence — speak with ownership: 'I decided', 'I led'.",
    "rp.worstFor.clarity": "Clarity — try slowing down and finishing each thought.",
    "rp.worstFor.grammar": "Grammar — watch tense agreement and prepositions.",
    "rp.worstFor.vocabulary": "Vocabulary — add 2-3 stronger words per answer.",
    "rp.worstFor.professional_tone": "Professional tone — swap casual phrases for polished ones.",
    "rp.worstFor.fluency": "Fluency — cut long pauses and filler words like 'um' and 'like'.",
    "rp.noStrengths": "Complete a session to see your strengths.",
    // Study-plan clipboard export labels.
    "sp.exportTitle": "Your interview prep plan",
    "sp.exportFocusH": "FOCUS AREAS",
    "sp.exportWhy": "Why",
    "sp.exportDaysH": "7-DAY PLAN",
    "sp.exportDay": "Day",
    "sp.exportEnglishPractice": "English practice",
  },
  "pt-BR": {
    "rp.loading": "Montando seu relatório…",
    "rp.copyOk": "Plano copiado para a área de transferência",
    "rp.copyFail": "Não deu para copiar — tente selecionar o texto manualmente.",
    "rp.planError": "Não foi possível criar o plano.",
    "rp.levelExcellent": "Excelente",
    "rp.levelStrong": "Forte",
    "rp.levelDeveloping": "Em Desenvolvimento",
    "rp.levelNeedsAttention": "Precisa de Atenção",
    "rp.levelPriority": "Prioridade de Desenvolvimento",
    "rp.copyExcellent":
      "Você demonstra um nível muito forte de comunicação profissional em inglês. Continue refinando suas respostas para manter esse padrão.",
    "rp.copyStrong":
      "Você apresenta uma comunicação sólida e profissional. Pequenos ajustes tornarão suas respostas ainda mais impactantes.",
    "rp.copyDeveloping":
      "Você possui uma boa base de inglês profissional. Com prática direcionada nas áreas destacadas neste relatório, poderá aumentar significativamente seu desempenho.",
    "rp.copyNeedsAttention":
      "Algumas competências precisam de maior desenvolvimento antes da sua próxima entrevista em inglês. Siga o plano de estudos para fortalecer essas áreas.",
    "rp.copyPriority":
      "Recomendamos dedicar mais tempo ao desenvolvimento das competências destacadas antes de participar de uma entrevista em inglês. O plano de estudos foi elaborado para guiá-lo(a) passo a passo.",
    "rp.bestFor.relevance": "Relevância à pergunta — você respondeu direto ao ponto.",
    "rp.bestFor.supporting_evidence": "Evidências e exemplos — você trouxe casos concretos que sustentam sua resposta.",
    "rp.bestFor.organization": "Organização e estrutura — sua resposta seguiu uma sequência lógica e clara.",
    "rp.bestFor.professional_competency": "Competência profissional — você demonstrou o conhecimento e comportamento esperados.",
    "rp.bestFor.evidence_of_results": "Evidência de resultados — você mostrou impacto real das suas ações.",
    "rp.bestFor.professional_presence": "Presença profissional — você comunicou com confiança e domínio.",
    "rp.bestFor.clarity": "Clareza — você fala de um jeito fácil de seguir.",
    "rp.bestFor.grammar": "Gramática — sua estrutura de frase está sólida.",
    "rp.bestFor.vocabulary": "Vocabulário — você usa as palavras certas.",
    "rp.bestFor.professional_tone": "Tom profissional — você soa como o cargo pede.",
    "rp.bestFor.fluency": "Fluência — sua fala flui de forma natural e sem hesitação.",
    "rp.worstFor.relevance": "Relevância à pergunta — leia a pergunta inteira e responda a todas as partes.",
    "rp.worstFor.supporting_evidence": "Evidências e exemplos — traga situações concretas, não descrições genéricas.",
    "rp.worstFor.organization": "Organização e estrutura — use uma sequência clara (contexto → ação → resultado).",
    "rp.worstFor.professional_competency": "Competência profissional — mostre decisões e conhecimento típicos da vaga.",
    "rp.worstFor.evidence_of_results": "Evidência de resultados — termine cada exemplo com o resultado ou impacto.",
    "rp.worstFor.professional_presence": "Presença profissional — fale com ownership: 'eu decidi', 'eu liderei'.",
    "rp.worstFor.clarity": "Clareza — desacelere e conclua cada ideia.",
    "rp.worstFor.grammar": "Gramática — cuidado com concordância e preposições.",
    "rp.worstFor.vocabulary": "Vocabulário — inclua 2-3 palavras mais fortes por resposta.",
    "rp.worstFor.professional_tone": "Tom profissional — troque frases casuais por versões mais polidas.",
    "rp.worstFor.fluency": "Fluência — reduza pausas longas e palavras de preenchimento como 'ãã' e 'like'.",
    "rp.noStrengths": "Complete uma sessão para ver seus pontos fortes.",
    "sp.exportTitle": "Seu plano de preparação para a entrevista",
    "sp.exportFocusH": "ÁREAS DE FOCO",
    "sp.exportWhy": "Por quê",
    "sp.exportDaysH": "PLANO DE 7 DIAS",
    "sp.exportDay": "Dia",
    "sp.exportEnglishPractice": "Prática em inglês",
  },
  "es-419": {
    "rp.loading": "Construyendo su informe…",
    "rp.copyOk": "Plan copiado al portapapeles",
    "rp.copyFail": "No se pudo copiar — intente seleccionar el texto manualmente.",
    "rp.planError": "No se pudo crear el plan.",
    "rp.levelExcellent": "Excelente",
    "rp.levelStrong": "Sólido",
    "rp.levelDeveloping": "En desarrollo",
    "rp.levelNeedsAttention": "Requiere atención",
    "rp.levelPriority": "Prioridad de desarrollo",
    "rp.copyExcellent":
      "Demuestra un nivel muy sólido de comunicación profesional en inglés. Siga puliendo sus respuestas para mantener este estándar.",
    "rp.copyStrong":
      "Presenta una comunicación sólida y profesional. Pequeños ajustes harán sus respuestas aún más impactantes.",
    "rp.copyDeveloping":
      "Tiene una buena base de inglés profesional. Con práctica dirigida en las áreas destacadas en este informe, podrá aumentar significativamente su desempeño.",
    "rp.copyNeedsAttention":
      "Algunas competencias necesitan más desarrollo antes de su próxima entrevista en inglés. Siga el plan de estudio para fortalecer estas áreas.",
    "rp.copyPriority":
      "Recomendamos dedicar más tiempo al desarrollo de las competencias destacadas antes de participar en una entrevista en inglés. El plan de estudio fue diseñado para guiarle paso a paso.",
    "rp.bestFor.relevance": "Relevancia a la pregunta — respondió directo al punto.",
    "rp.bestFor.supporting_evidence": "Evidencia y ejemplos — respaldó su respuesta con casos concretos.",
    "rp.bestFor.organization": "Organización — su respuesta siguió una secuencia lógica y clara.",
    "rp.bestFor.professional_competency": "Competencia profesional — demostró el conocimiento y comportamiento esperados.",
    "rp.bestFor.evidence_of_results": "Evidencia de resultados — mostró el impacto real de sus acciones.",
    "rp.bestFor.professional_presence": "Presencia profesional — comunicó con confianza y dominio.",
    "rp.bestFor.clarity": "Claridad — habla de una forma fácil de seguir.",
    "rp.bestFor.grammar": "Gramática — su estructura de frase es sólida.",
    "rp.bestFor.vocabulary": "Vocabulario — usa las palabras adecuadas.",
    "rp.bestFor.professional_tone": "Tono profesional — suena como lo pide el puesto.",
    "rp.bestFor.fluency": "Fluidez — su inglés hablado fluye de forma natural sin dudas innecesarias.",
    "rp.worstFor.relevance": "Relevancia a la pregunta — lea toda la pregunta y responda cada parte.",
    "rp.worstFor.supporting_evidence": "Evidencia y ejemplos — use situaciones concretas, no descripciones genéricas.",
    "rp.worstFor.organization": "Organización — use una secuencia clara (contexto → acción → resultado).",
    "rp.worstFor.professional_competency": "Competencia profesional — muestre decisiones y conocimiento típicos del puesto.",
    "rp.worstFor.evidence_of_results": "Evidencia de resultados — termine cada ejemplo con el resultado o impacto.",
    "rp.worstFor.professional_presence": "Presencia profesional — hable con ownership: 'yo decidí', 'yo lideré'.",
    "rp.worstFor.clarity": "Claridad — desacelere y termine cada idea.",
    "rp.worstFor.grammar": "Gramática — cuide la concordancia y las preposiciones.",
    "rp.worstFor.vocabulary": "Vocabulario — agregue 2-3 palabras más fuertes por respuesta.",
    "rp.worstFor.professional_tone": "Tono profesional — cambie frases casuales por versiones más pulidas.",
    "rp.worstFor.fluency": "Fluidez — reduzca pausas largas y muletillas como 'eh' y 'like'.",
    "rp.noStrengths": "Complete una sesión para ver sus fortalezas.",
    "sp.exportTitle": "Su plan de preparación para la entrevista",
    "sp.exportFocusH": "ÁREAS DE ENFOQUE",
    "sp.exportWhy": "Por qué",
    "sp.exportDaysH": "PLAN DE 7 DÍAS",
    "sp.exportDay": "Día",
    "sp.exportEnglishPractice": "Práctica en inglés",
  },
  "fr-CA": {
    "rp.loading": "Construction de votre rapport…",
    "rp.copyOk": "Plan copié dans le presse-papiers",
    "rp.copyFail": "Impossible de copier — essayez de sélectionner le texte manuellement.",
    "rp.planError": "Impossible de créer le plan.",
    "rp.levelExcellent": "Excellent",
    "rp.levelStrong": "Solide",
    "rp.levelDeveloping": "En développement",
    "rp.levelNeedsAttention": "À surveiller",
    "rp.levelPriority": "Priorité de développement",
    "rp.copyExcellent":
      "Vous démontrez un niveau très solide de communication professionnelle en anglais. Continuez à peaufiner vos réponses pour maintenir ce standard.",
    "rp.copyStrong":
      "Vous présentez une communication solide et professionnelle. De petits ajustements rendront vos réponses encore plus percutantes.",
    "rp.copyDeveloping":
      "Vous avez une bonne base d’anglais professionnel. Avec une pratique ciblée dans les domaines mis en évidence dans ce rapport, vous pouvez améliorer significativement votre performance.",
    "rp.copyNeedsAttention":
      "Certaines compétences demandent plus de développement avant votre prochaine entrevue en anglais. Suivez le plan d’étude pour renforcer ces domaines.",
    "rp.copyPriority":
      "Nous vous recommandons de consacrer plus de temps au développement des compétences mises en évidence avant de passer une entrevue en anglais. Le plan d’étude est conçu pour vous guider pas à pas.",
    "rp.bestFor.relevance": "Pertinence — vous avez répondu directement à la question.",
    "rp.bestFor.supporting_evidence": "Preuves et exemples — vous avez appuyé votre réponse avec des cas concrets.",
    "rp.bestFor.organization": "Organisation — votre réponse a suivi une séquence logique et claire.",
    "rp.bestFor.professional_competency": "Compétence professionnelle — vous avez démontré les connaissances et comportements attendus.",
    "rp.bestFor.evidence_of_results": "Preuve de résultats — vous avez montré l’impact réel de vos actions.",
    "rp.bestFor.professional_presence": "Présence professionnelle — vous avez communiqué avec confiance et maîtrise.",
    "rp.bestFor.clarity": "Clarté — vous parlez d’une façon facile à suivre.",
    "rp.bestFor.grammar": "Grammaire — votre structure de phrase est solide.",
    "rp.bestFor.vocabulary": "Vocabulaire — vous utilisez les bons mots.",
    "rp.bestFor.professional_tone": "Ton professionnel — vous sonnez comme le poste l’exige.",
    "rp.bestFor.fluency": "Aisance — votre anglais parlé s’écoule naturellement sans hésitation inutile.",
    "rp.worstFor.relevance": "Pertinence — lisez toute la question et répondez à chaque partie.",
    "rp.worstFor.supporting_evidence": "Preuves et exemples — utilisez des situations concrètes, pas des descriptions génériques.",
    "rp.worstFor.organization": "Organisation — utilisez une séquence claire (contexte → action → résultat).",
    "rp.worstFor.professional_competency": "Compétence professionnelle — montrez des décisions et connaissances typiques du poste.",
    "rp.worstFor.evidence_of_results": "Preuve de résultats — terminez chaque exemple avec le résultat ou l’impact.",
    "rp.worstFor.professional_presence": "Présence professionnelle — parlez avec ownership : ’j’ai décidé’, ’j’ai dirigé’.",
    "rp.worstFor.clarity": "Clarté — ralentissez et terminez chaque idée.",
    "rp.worstFor.grammar": "Grammaire — attention à l’accord des temps et aux prépositions.",
    "rp.worstFor.vocabulary": "Vocabulaire — ajoutez 2-3 mots plus forts par réponse.",
    "rp.worstFor.professional_tone": "Ton professionnel — remplacez les phrases familières par des versions plus soignées.",
    "rp.worstFor.fluency": "Aisance — coupez les longues pauses et les mots de remplissage comme ’euh’ et ’like’.",
    "rp.noStrengths": "Complétez une séance pour voir vos points forts.",
    "sp.exportTitle": "Votre plan de préparation à l’entrevue",
    "sp.exportFocusH": "DOMAINES À TRAVAILLER",
    "sp.exportWhy": "Pourquoi",
    "sp.exportDaysH": "PLAN DE 7 JOURS",
    "sp.exportDay": "Jour",
    "sp.exportEnglishPractice": "Pratique en anglais",
  },
};

export function reportT(lang) {
  const code = normalizeLang(lang);
  const table = REPORT_STRINGS[code] || REPORT_STRINGS.en;
  return (key) => {
    const v = table[key];
    if (v !== undefined) return v;
    // Approved fallback is ENGLISH, never Portuguese.
    return REPORT_STRINGS.en[key] ?? key;
  };
}

// Level bucket → i18n key. Kept in one place so the label and copy stay
// in lockstep.
export function levelKey(score) {
  const v = Number(score) || 0;
  if (v >= 90) return "Excellent";
  if (v >= 75) return "Strong";
  if (v >= 55) return "Developing";
  if (v >= 35) return "NeedsAttention";
  return "Priority";
}

export function levelHeadline(score, tr) {
  return tr("rp.level" + levelKey(score));
}

export function levelCopy(score, tr) {
  return tr("rp.copy" + levelKey(score));
}
